import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import { LoadingButton } from "../../components/ui/Button";
import { TextAreaInput } from "../../components/ui/form/TextAreaInput";
import { SelectInput } from "../../components/ui/form/SelectInput";
import { RadioGroup, RadioGroupItem, RadioGroupItemInput, RadioGroupItemControl, RadioGroupItemLabel } from "@/components/shadcn/radio-group";
import { onMount, For, Show, createEffect, createSignal } from "solid-js";
import useCheckupAppointment from "../../services/checkup/useCheckupAppointment";
import useCheckupHistory from "../../services/checkup/useCheckupHistory";
import { Button } from "@/components/shadcn/button";

export default function CheckupNoteHistory(props) {
  // Appointment note related code
  const { 
    appointmentForm, 
    onSaveAppointment, 
    handleAppointmentInput 
  } = useCheckupAppointment(props);

  // History related code
  const { 
    historyForm, 
    onSaveHistory, 
    onFetchHistoryQuestions, 
    updateHistoryAnswer, 
    clearHistoryAnswer 
  } = useCheckupHistory(props);

  const [isInitialized, setIsInitialized] = createSignal(false);

  onMount(() => {
    // Fetch history questions
    onFetchHistoryQuestions();
    
    // Register both save functions with parent component
    if (props.onRegisterSave) {
      // Create a combined save function that saves both note and history
      props.onRegisterSave(async () => {
        await onSaveAppointment();
        await onSaveHistory();
      });
    }
    
    // Set initialized flag after a brief delay to ensure props are stable
    setTimeout(() => setIsInitialized(true), 100);
  });

  // Track form changes for both components
  createEffect(() => {
    if (props.onFormChange && isInitialized()) {
      // Check appointment note changes
      const originalNotes = props.appointment?.notes || "";
      const currentNotes = appointmentForm.notes || "";
      const hasNoteChanges = originalNotes !== currentNotes;
      
      // Check history changes
      let hasHistoryChanges = false;
      historyForm.histories.forEach((currentHistory) => {
        const originalAnswer = currentHistory.originalAnswer;
        const currentAnswer = currentHistory.answer;
        
        if (originalAnswer !== currentAnswer) {
          hasHistoryChanges = true;
        }
      });
      
      // Report changes to parent if either has changes
      props.onFormChange(hasNoteChanges || hasHistoryChanges);
    }
  });

  const renderHistoryInput = (history) => {
    const options = history.options || [];
    
    if (options.length === 0) {
      // No options available, this shouldn't happen as all questions should have options
      return (
        <div class="text-sm text-gray-500 dark:text-gray-400">
          No options available for this question
        </div>
      );
    }

    if (options.length <= 3) {
      // Few options, render as shadcn radio buttons inline with title
      return (
        <div class="flex items-center gap-4 flex-wrap">
          <RadioGroup 
            value={history.answer || ""} 
            onChange={(value) => updateHistoryAnswer(history.key, value)}
            class="flex flex-row gap-2"
          >
            <For each={options}>
              {(option) => (
                <div class="flex items-center space-x-2 me-2">
                  <RadioGroupItem class="flex gap-1" value={option}>
                    <RadioGroupItemInput />
                    <RadioGroupItemControl />
                    <RadioGroupItemLabel class="ms-1.5">{option}</RadioGroupItemLabel>
                  </RadioGroupItem>
                </div>
              )}
            </For>
          </RadioGroup>
        </div>
      );
    } else {
      // Many options, render as select dropdown
      return (
        <div class="w-full">
          <SelectInput
            label={false}
            name={`history-${history.key}`}
            form={{ [`history-${history.key}`]: history.answer || "" }}
            input={(_, value) => updateHistoryAnswer(history.key, value)}
            options={options.map(option => ({ value: option, text: option }))}
            placeholder="Select an answer..."
          />
        </div>
      );
    }
  };

  const content = (
    <>
      <Show when={!props.hideCard}>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Icon icon="mdi:note-text" class="text-gray-500 me-2" />
            Checkup
          </CardTitle>
        </CardHeader>
      </Show>
      
      <div class={props.hideCard ? "space-y-4" : ""}>
        {/* Appointment Note Section */}
        <div>
          <h4 class="font-medium text-gray-900 mb-3 dark:text-gray-100">
            Appointment Note
          </h4>
          <div>
            <TextAreaInput
              label={false}
              name="notes"
              form={appointmentForm}
              input={handleAppointmentInput}
              rows={5}
              placeholder="Enter appointment note..."
            />
          </div>
        </div>
        
        {/* History Section */}
        <div class="border-t pt-4 mt-4">
          <h4 class="font-medium text-gray-900 mb-4 dark:text-gray-100">
            Patient History
          </h4>
          
          <div class="space-y-4">
            <For each={historyForm.histories}>
              {(history) => (
                <div class="border border-gray-200 rounded-md p-4 dark:border-gray-700">
                  <div class="flex flex-col gap-3">
                    <div class="flex justify-between items-start">
                      <h5 class="font-medium text-gray-800 dark:text-gray-200">{history.question}</h5>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => clearHistoryAnswer(history.key)}
                        class="text-gray-500 hover:text-gray-700 p-1 h-7 w-7"
                      >
                        <Icon icon="tabler:x" class="h-4 w-4" />
                      </Button>
                    </div>
                    {renderHistoryInput(history)}
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>

        {/* Save Button */}
        <Show when={!props.hideSaveButton}>
          <div class="border-t pt-4 mt-4">
            <LoadingButton
              loading={props.loading}
              onClick={async () => {
                await onSaveAppointment();
                await onSaveHistory();
              }}
            >
              Save Note & History
            </LoadingButton>
          </div>
        </Show>
      </div>
    </>
  );

  return (
    <Show when={props.hideCard} fallback={<Card><CardContent class="space-y-4">{content}</CardContent></Card>}>
      {content}
    </Show>
  );
}
